import cornerstoneWADOImageLoader from "cornerstone-wado-image-loader";
import {
  processOPTDicomMetaData,
  processOPDicomMetaData,
  processElement,
  calc_angle_deg,
  logMetadataComparison,
} from "../utils/dicom_utils";
import cornerstone from "cornerstone-core";
import cornerstoneTools from "cornerstone-tools";
import axios from "axios";
import { toPng } from "html-to-image";
import { API_URL } from "../../../Endpoints";
import { getAiSegmentationData } from "../Viewers/utils";
import { buildFluidBscansForVolumesApi } from "../utils/fluidFrameState";
import {
  getFluidLabelsForVolumesApi,
  getFluidLabelsFromVolumesData,
  isTopconManufacturer,
} from "../utils/fluidManualBrush";
import { applyTopconOptDisplayStretchToStack } from "../utils/topconOptDisplayStretch";
import store from "../../../store";
import {
  serializeAnnotationsForSave,
  hasCompactOrLegacyEtdrsVolumesData,
} from "../utils/annotationFormat";
// Cache to store metadata results - persists for entire app runtime
const metadataCache = new Map();
const metadataRequestCache = new Map();

/** Incremented when adjudicator switches annotation source; stale compute-volumes responses are ignored. */
let annotationSourceVolumesToken = 0;

export function bumpAnnotationSourceVolumesToken() {
  annotationSourceVolumesToken += 1;
  return annotationSourceVolumesToken;
}

function isAnnotationSourceVolumesTokenCurrent(token) {
  return token === annotationSourceVolumesToken;
}

/** Tracks in-flight save-annotations requests. */
const pendingAnnotationSave = {
  controller: null,
  generation: 0,
  promise: null,
};

function isAnnotationSaveAborted(err) {
  return (
    err?.code === "ERR_CANCELED" ||
    err?.name === "CanceledError" ||
    (typeof axios.isCancel === "function" && axios.isCancel(err))
  );
}

function setAnnotationSaveStatus({
  active,
  percent = null,
  etaSeconds = null,
  message = "",
}) {
  store.dispatch({
    type: "SET_ANNOTATION_SAVE_STATUS",
    active: Boolean(active),
    percent,
    etaSeconds,
    message,
  });
}

function updateAnnotationSaveProgress(
  generation,
  { percent, etaSeconds, message },
) {
  if (generation !== pendingAnnotationSave.generation) return;
  setAnnotationSaveStatus({
    active: true,
    percent,
    etaSeconds,
    message,
  });
}

function beginAnnotationSave() {
  store.dispatch({ type: "LoadingOverlay_changed", LoadingOverlay: false });

  const controller = new AbortController();
  const generation = pendingAnnotationSave.generation + 1;
  pendingAnnotationSave.generation = generation;
  pendingAnnotationSave.controller = controller;

  setAnnotationSaveStatus({
    active: true,
    percent: 0,
    etaSeconds: null,
    message: "Preparing save request...",
  });

  return { controller, generation };
}

function abortPendingAnnotationSave() {
  if (pendingAnnotationSave.controller) {
    pendingAnnotationSave.controller.abort();
  }
}

function finishAnnotationSave(generation) {
  if (generation !== pendingAnnotationSave.generation) return;
  pendingAnnotationSave.controller = null;
  setAnnotationSaveStatus({
    active: false,
    percent: null,
    etaSeconds: null,
    message: "",
  });
}

function createAnnotationSaveUploadProgressHandler(generation) {
  const uploadStartedAt = Date.now();

  return (progressEvent) => {
    if (generation !== pendingAnnotationSave.generation) return;

    const loaded = Number(progressEvent?.loaded ?? 0);
    const total = Number(progressEvent?.total ?? 0);

    if (!total || total <= 0) {
      updateAnnotationSaveProgress(generation, {
        percent: null,
        etaSeconds: null,
        message: "Uploading annotations...",
      });
      return;
    }

    const uploadRatio = Math.min(1, loaded / total);
    const percent = Math.round(5 + uploadRatio * 90);
    const elapsedSec = Math.max(0.001, (Date.now() - uploadStartedAt) / 1000);
    let etaSeconds = null;
    let message = "Uploading annotations...";

    if (loaded > 0) {
      const bytesPerSecond = loaded / elapsedSec;
      if (bytesPerSecond > 0) {
        if (loaded < total) {
          etaSeconds = (total - loaded) / bytesPerSecond;
        } else {
          message = "Processing on server...";
          etaSeconds = Math.max(2, elapsedSec * 0.75);
        }
      }
    }

    updateAnnotationSaveProgress(generation, {
      percent: loaded >= total ? Math.min(percent, 98) : percent,
      etaSeconds,
      message,
    });
  };
}

const DEFAULT_PIXEL_SPACING = [1, 1];

const normalizeNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const normalizeSpacingArray = (value) => {
  if (Array.isArray(value) && value.length >= 2) {
    return value.map((entry) => normalizeNumber(entry));
  }
  if (typeof value === "string" && value.includes("\\")) {
    return value.split("\\").map((entry) => normalizeNumber(entry));
  }
  return null;
};

function getPixelSpacingFromMeta(opMetaData) {
  const spacingEntry = opMetaData?.find?.(
    (obj) => obj.name === "Pixel Spacing",
  );
  if (!spacingEntry) {
    return null;
  }
  const spacing = normalizeSpacingArray(spacingEntry.Value);
  if (
    spacing &&
    spacing.length >= 2 &&
    spacing.every((entry) => entry !== null)
  ) {
    return spacing;
  }
  return null;
}

function getPixelSpacingFromDicomData(opData) {
  const spacingSource =
    opData?.OP?.metadata?.pixel_spacing ||
    opData?.OPT?.metadata?.pixel_spacing ||
    null;

  if (!spacingSource) {
    return null;
  }

  if (Array.isArray(spacingSource) && spacingSource.length >= 2) {
    const parsed = spacingSource
      .slice(0, 2)
      .map((entry) => normalizeNumber(entry));
    if (parsed.every((entry) => entry !== null)) {
      return parsed;
    }
  }

  const row =
    spacingSource.rowPixelSpacing ??
    spacingSource.RowPixelSpacing ??
    spacingSource.row ??
    spacingSource.Row ??
    spacingSource[0];
  const column =
    spacingSource.columnPixelSpacing ??
    spacingSource.ColumnPixelSpacing ??
    spacingSource.column ??
    spacingSource.Column ??
    spacingSource[1];

  const normalizedRow = normalizeNumber(row);
  const normalizedColumn = normalizeNumber(column);

  if (normalizedRow !== null && normalizedColumn !== null) {
    return [normalizedRow, normalizedColumn];
  }

  return null;
}

export function isOpManufacturerMetadataReady(opMetaData) {
  if (!opMetaData || !Array.isArray(opMetaData) || opMetaData.length === 0) {
    return false;
  }
  return opMetaData.some(
    (obj) => obj.id === "00080070" || obj.name === "Manufacturer",
  );
}

export function getOpManufacturer(opMetaData) {
  if (!opMetaData || !Array.isArray(opMetaData)) return "";
  const entry = opMetaData.find(
    (obj) => obj.id === "00080070" || obj.name === "Manufacturer",
  );
  return String(entry?.Value || "").trim();
}

const OP_MANUFACTURER_METADATA_POLL_MS = 100;
const OP_MANUFACTURER_METADATA_TIMEOUT_MS = 30000;

function waitForOpManufacturerMetadata(
  timeoutMs = OP_MANUFACTURER_METADATA_TIMEOUT_MS,
  intervalMs = OP_MANUFACTURER_METADATA_POLL_MS,
) {
  return new Promise((resolve, reject) => {
    const startedAt = Date.now();

    const poll = () => {
      const opMetaData = store.getState().dcm?.opMetaData;
      if (isOpManufacturerMetadataReady(opMetaData)) {
        resolve(opMetaData);
        return;
      }
      if (Date.now() - startedAt >= timeoutMs) {
        reject(new Error("OP manufacturer metadata not available"));
        return;
      }
      setTimeout(poll, intervalMs);
    };

    poll();
  });
}

function getVendorCalibratedPixelSpacing(opMetaData) {
  if (!opMetaData || !Array.isArray(opMetaData)) {
    return null;
  }

  const getById = (id) => opMetaData.find((obj) => obj.id === id)?.Value;

  const manufacturer = getById("00080070"); // Manufacturer
  const modelName = getById("00081090"); // Manufacturer's Model Name
  const horizontalFOV = getById("0022000C"); // Horizontal Field of View
  const rows = getById("00280010"); // Rows
  const cols = getById("00280011"); // Columns

  // Example: Heidelberg Spectralis 30° scan, 1536 x 1536 pixels
  if (
    manufacturer === "Heidelberg Engineering" &&
    modelName === "Spectralis" &&
    Number(horizontalFOV) === 30 &&
    Number(rows) === 1536 &&
    Number(cols) === 1536
  ) {
    // Approximate retinal scaling: ~0.3 mm per degree (domain-specific)
    const MM_PER_DEGREE = 0.3;
    const widthMM = Number(horizontalFOV) * MM_PER_DEGREE;
    const spacing = widthMM / Number(cols);

    if (Number.isFinite(spacing) && spacing > 0) {
      return [spacing, spacing];
    }
  }

  return null;
}

function getOpPixelSpacing(opMetaData, opData) {
  // 1) Exact DICOM metadata from server, if present
  const fromMeta = getPixelSpacingFromMeta(opMetaData);
  if (fromMeta) {
    return fromMeta;
  }

  // 2) Vendor‑specific calibration (e.g. Heidelberg Spectralis 30°)
  const fromVendor = getVendorCalibratedPixelSpacing(opMetaData);
  if (fromVendor) {
    return fromVendor;
  }

  // 3) Parsed from Cornerstone/OP/OPT metadata, but treat [1,1] as "unknown"
  const fromData = getPixelSpacingFromDicomData(opData);
  if (fromData && !(fromData[0] === 1 && fromData[1] === 1)) {
    return fromData;
  }

  // 4) Final fallback
  return DEFAULT_PIXEL_SPACING;
}

function getOpImageSize(opMetaData, opData) {
  const columns = normalizeNumber(
    opMetaData?.find?.((obj) => obj.name === "Columns")?.Value,
  );
  const rows = normalizeNumber(
    opMetaData?.find?.((obj) => obj.name === "Rows")?.Value,
  );

  if (columns !== null && rows !== null) {
    return [columns, rows];
  }

  const fallbackWidth =
    opData?.OP?.metadata?.width ??
    opData?.OP?.metadata?.meta?.width ??
    opData?.OPT?.metadata?.meta?.width ??
    null;
  const fallbackHeight =
    opData?.OP?.metadata?.height ??
    opData?.OP?.metadata?.meta?.height ??
    opData?.OPT?.metadata?.meta?.height ??
    null;

  const normalizedWidth = normalizeNumber(fallbackWidth);
  const normalizedHeight = normalizeNumber(fallbackHeight);

  if (normalizedWidth !== null && normalizedHeight !== null) {
    return [normalizedWidth, normalizedHeight];
  }

  return [0, 0];
}

const asciiByteArrayToString = (byteArray) =>
  byteArray.reduce((acc, byte) => acc + String.fromCharCode(byte), "");

function parseSpacingElement(element, byteArray) {
  if (!element) {
    return null;
  }
  const rawString = asciiByteArrayToString(
    byteArray.slice(element.dataOffset, element.dataOffset + element.length),
  );
  const [row, column] = rawString.split("\\").map((value) => parseFloat(value));
  if (Number.isFinite(row) && Number.isFinite(column)) {
    return [row, column];
  }
  return null;
}

function parseSpacingFromFunctionalGroup(dataSet) {
  const functionalGroups = dataSet?.elements?.x52009229?.items;
  if (!functionalGroups || functionalGroups.length === 0) {
    return null;
  }
  const pixelMeasures =
    functionalGroups[0]?.dataSet?.elements?.x00289110?.items;
  if (!pixelMeasures || pixelMeasures.length === 0) {
    return null;
  }
  const element = pixelMeasures[0]?.dataSet?.elements?.x00280030;
  return parseSpacingElement(element, dataSet.byteArray);
}

function parseSpacingFromDataSet(dataSet) {
  if (!dataSet) {
    return null;
  }
  const directSpacing = parseSpacingElement(
    dataSet.elements?.x00280030,
    dataSet.byteArray,
  );
  if (directSpacing) {
    return directSpacing;
  }
  return parseSpacingFromFunctionalGroup(dataSet);
}

const pixelSpacingCache = new Map();

async function fetchPixelSpacingFromDicom(url) {
  if (!url) {
    return null;
  }
  if (pixelSpacingCache.has(url)) {
    return pixelSpacingCache.get(url);
  }
  try {
    const dataSet =
      await cornerstoneWADOImageLoader.wadouri.dataSetCacheManager.load(
        url,
        cornerstoneWADOImageLoader.internal.xhrRequest,
      );
    const spacing = parseSpacingFromDataSet(dataSet);
    if (spacing) {
      pixelSpacingCache.set(url, spacing);
      return spacing;
    }
  } catch (error) {
    console.error("Failed to fetch pixel spacing from DICOM URL:", url, error);
  }
  return null;
}

async function ensureOpPixelSpacing(opMetaData, opData) {
  const cachedSpacing = getOpPixelSpacing(opMetaData, opData);
  if (cachedSpacing) {
    return cachedSpacing;
  }

  const candidateUrls = [
    opData?.OP?.url,
    opData?.OP?.metadata?.url,
    opData?.OPT?.metadata?.url,
    opData?.OPT?.url,
  ].filter(Boolean);

  for (const url of candidateUrls) {
    const spacing = await fetchPixelSpacingFromDicom(url);
    if (spacing) {
      return spacing;
    }
  }

  console.warn("Falling back to default pixel spacing for stack", opData);
  return DEFAULT_PIXEL_SPACING;
}

function check_followup_dicom(base_dcm_data, child_dcm_data, dcm_id) {
  /*
    Checks if child dicom is a follow up scan of base dicom file
  */
  if (child_dcm_data === null) {
    return true;
  }
  var isFollowUp = true;
  if (
    base_dcm_data.metadata.frames.length !==
    child_dcm_data.metadata.frames.length
  ) {
    // If number of frames in both dicoms are not equal, it is not followup
    isFollowUp = false;
  } else {
    // If number of frames in both dicoms are equal, compare slice coordinates for each frame and if they are on same place, mark as followup
    for (var i = 0; i < base_dcm_data.metadata.frames.length; i++) {
      if (
        base_dcm_data.metadata.frames[i][0] !==
        child_dcm_data.metadata.frames[i][0]
      ) {
        // Check first point
        isFollowUp = false;
      }
    }
  }
  return isFollowUp;
}

export async function fetch_dicom_metdata(
  dicom_urls,
  scheme,
  dcm_id,
  dispatch,
  dicom_data,
) {
  let OPT_DATA = null;
  let OP_DATA = null;
  await cornerstoneWADOImageLoader.wadouri.dataSetCacheManager
    .load(dicom_urls[0], cornerstoneWADOImageLoader.internal.xhrRequest)
    .then(function (dataSet) {
      if (
        processElement(dataSet.elements.x00080060, dataSet.byteArray).trim() ===
        "OPT"
      ) {
        // If the image is an OCT (OPT)
        OPT_DATA = processOPTDicomMetaData(dataSet, scheme, dicom_urls[0]);
        OPT_DATA.image_id = "wadouri:" + dicom_urls[0];
        OPT_DATA.url = dicom_urls[0];
      } else {
        // If it is not an OPPT, then it is an OP
        OP_DATA = processOPDicomMetaData(dataSet, scheme, dicom_urls[0]);
        OP_DATA.image_id = "wadouri:" + dicom_urls[0];
        OP_DATA.url = dicom_urls[0];
      }
    });

  await cornerstoneWADOImageLoader.wadouri.dataSetCacheManager
    .load(dicom_urls[1], cornerstoneWADOImageLoader.internal.xhrRequest)
    .then(function (dataSet) {
      if (
        processElement(dataSet.elements.x00080060, dataSet.byteArray).trim() ===
        "OPT"
      ) {
        // If the image is an OCT (OPT)
        OPT_DATA = processOPTDicomMetaData(dataSet, scheme, dicom_urls[1]);
        OPT_DATA.image_id = "wadouri:" + dicom_urls[1];
        OPT_DATA.url = dicom_urls[1];
      } else {
        // If it is not an OPPT, then it is an OP
        OP_DATA = processOPDicomMetaData(dataSet, scheme, dicom_urls[1]);
        OP_DATA.image_id = "wadouri:" + dicom_urls[1];
        OP_DATA.url = dicom_urls[1];
      }
    });

  let angle = calc_angle_deg(
    OPT_DATA.metadata.frames[OPT_DATA.metadata.frames.length - 1],
    OP_DATA.metadata.pixel_spacing,
  );
  OPT_DATA.metadata.rect_angle = angle;

  var parentDcmData = null;
  // If there is already a stack added, we check if the new stack is a followup or not
  if (Object.keys(dicom_data).length > 0) {
    Object.keys(dicom_data).forEach(function (key) {
      if (key !== dcm_id) {
        parentDcmData = dicom_data[key].OPT;
      }
    });
  } else {
  }

  let isFollowUp = check_followup_dicom(OPT_DATA, parentDcmData, dcm_id);
  if (Object.keys(dicom_data).length > 0) {
    dispatch({
      type: "DICOM_CAN_SYNC_SCANS",
      canSyncScans: isFollowUp,
    });
  }

  applyTopconOptDisplayStretchToStack(OP_DATA, OPT_DATA);

  dispatch({
    type: "DICOM_FETCH_META_SUCCESS",
    dicom_data: {
      OP: OP_DATA,
      OPT: OPT_DATA,
      loading: false,
      isValidDcm: isFollowUp,
    },
    id: dcm_id,
  });
}

function get_OP_image_data(singleStack) {
  let imageAnnotations = singleStack?.annotations;
  var annotation_data = {};
  Object.entries(imageAnnotations).forEach(([key, value]) => {
    if (value !== undefined) {
      annotation_data[key] = value;
    }
  });

  return annotation_data;
}

export function ezdalinescheck(inputArray) {
  for (const frameUrl in inputArray) {
    if (inputArray.hasOwnProperty(frameUrl)) {
      const frameData = inputArray[frameUrl];
      const lisonItems = Array.isArray(frameData.Lison)
        ? frameData.Lison
        : frameData.Lison?.data;

      if (lisonItems && lisonItems.length > 0) {
        return true;
      }
    }
  }

  return false;
}

export function extractPointsCoordinates(inputArray) {
  const points_coordinates = [];

  for (const frameUrl in inputArray) {
    if (inputArray.hasOwnProperty(frameUrl)) {
      const frameData = inputArray[frameUrl];

      const lisonItems = Array.isArray(frameData.Lison)
        ? frameData.Lison
        : frameData.Lison?.data;

      if (lisonItems && lisonItems.length > 0) {
        const frameNumber = frameUrl.match(/frame=(\d+)/)[1];

        const linesData = lisonItems.map((line, index) => {
          const startX =
            line.start?.[0] ??
            line.handles?.start?.x ??
            line.handles?.start?.[0];
          const endX =
            line.end?.[0] ?? line.handles?.end?.x ?? line.handles?.end?.[0];
          return {
            line_id: index + 1,
            start_x_coor: Math.floor(startX),
            end_x_coor: Math.floor(endX),
          };
        });

        points_coordinates.push({
          b_scan_id: parseInt(frameNumber),
          lines: linesData,
        });
      }
    }
  }

  return points_coordinates;
}
function get_OPT_image_data(singleStack, drawingTools) {
  var imgIds = singleStack.stack.imageIds;
  var frames = {};
  for (var i = 0; i < imgIds.length; i++) {
    // Get frame annotations for all tools
    var frameAnnotations = {};
    var containsAnyData = false;
    drawingTools.forEach(function (tool) {
      var toolAnnotations =
        cornerstoneTools.globalImageIdSpecificToolStateManager.getImageIdToolState(
          imgIds[i],
          tool,
        );

      if (toolAnnotations !== undefined) {
        frameAnnotations[tool] = toolAnnotations;
        containsAnyData = true;
      }
    });
    if (containsAnyData) {
      frames[imgIds[i]] = frameAnnotations;
    }
  }
  return frames;
}

export function get_annotations(dicom_data, drawingTools) {
  /*
    Gets annotations from dicom data
  */
  var annotations = {};
  Object.entries(dicom_data).forEach(([key, singleStack]) => {
    let brushToolData = {};

    var stack_data = {
      OP: get_OP_image_data(singleStack.OP),
      OPT: get_OPT_image_data(singleStack.OPT, drawingTools),
      annots: brushToolData,
      meta: singleStack.OPT.metadata.meta,
    };

    annotations[key] = stack_data;
  });

  return annotations;
}
export function veriticalMaxDistance(pointsArray) {
  const verticalDistances = [];
  for (let i = 1; i < pointsArray.length; i++) {
    const startYDistance = pointsArray[i - 1][1] - pointsArray[i][1];
    const endYDistance = pointsArray[i - 1][3] - pointsArray[i][3];
    verticalDistances.push([startYDistance, endYDistance]);
  }

  const flattenedData = verticalDistances.flat();
  const maxValue = Math.max(...flattenedData);

  return maxValue;
}

async function dicomMetadataFetch(url) {
  if (!url) return null;

  const cached = metadataCache.get(url);
  if (Array.isArray(cached) && cached.length > 0) {
    return cached;
  }

  const inFlight = metadataRequestCache.get(url);
  if (inFlight) {
    return inFlight;
  }

  const request = axios
    .post(`${API_URL}/api/v1/dicom/meta`, {
      url,
    })
    .then((resp) => {
      if (Array.isArray(resp.data) && resp.data.length > 0) {
        metadataCache.set(url, resp.data);
      }
      return resp.data ?? null;
    })
    .catch((err) => {
      console.error("Error fetching metadata for:", url, err);
      return null;
    })
    .finally(() => {
      metadataRequestCache.delete(url);
    });

  metadataRequestCache.set(url, request);
  return request;
}

export async function opMetadataFetch(opUrl) {
  return dicomMetadataFetch(opUrl);
}

export async function optMetadataFetch(optUrl) {
  return dicomMetadataFetch(optUrl);
}

export function opPoints(lisonLines, opData, starting_points, ending_points) {
  for (let i = 0; i < lisonLines.length; i++) {
    for (let j = 0; j < lisonLines[i].lines.length; j++) {
      let frame_coords = opData.OPT.metadata.frames[lisonLines[i].b_scan_id];

      let optWidth = opData.OPT?.metadata?.meta?.width;

      let Framecoords = opData?.OPT?.metadata?.frames[lisonLines[i]?.b_scan_id];

      const dx = Framecoords[2] - Framecoords[0];
      const dy = Framecoords[3] - Framecoords[1];
      let redLineLength = Math.sqrt(dx * dx + dy * dy);

      let scalingFactor = redLineLength / optWidth;

      // calculate the coordinates of the point on the oblique line placed in cartesian grid by knowing the distance from starting poing

      // step : 1 calculating slope of line
      // const slope =
      //   (frame_coords[3] - frame_coords[1]) /
      //   (frame_coords[2] - frame_coords[0]);

      // step : 2 Direction vector (x2-x1,y2-y1)
      // let directionVector = [
      //   frame_coords[2] - frame_coords[0],
      //   frame_coords[3] - frame_coords[1],
      // ];

      // step :3 Length of direction vector sqrt((x2-x1)^2,(y2-y1)^2) which is same as redLineLength we already calculated above

      // step : 4 Unit vector (x2-x1/length,y2-y1/length)
      let unitVector = [
        (frame_coords[2] - frame_coords[0]) / redLineLength,
        (frame_coords[3] - frame_coords[1]) / redLineLength,
      ];

      let newStartPoint = [
        frame_coords[0] +
          lisonLines[i].lines[j].start_x_coor * scalingFactor * unitVector[0],

        frame_coords[1] +
          lisonLines[i].lines[j].start_x_coor * scalingFactor * unitVector[1],
      ];

      let newEndPoint = [
        frame_coords[0] +
          lisonLines[i].lines[j].end_x_coor * scalingFactor * unitVector[0],

        frame_coords[1] +
          lisonLines[i].lines[j].end_x_coor * scalingFactor * unitVector[1],
      ];

      starting_points.push([
        parseInt(newStartPoint[0]),
        parseInt(newStartPoint[1]),
      ]);

      ending_points.push([parseInt(newEndPoint[0]), parseInt(newEndPoint[1])]);
    }
  }
}

async function fetchMlLisonData(payload) {
  try {
    const resp = await axios.post(`${API_URL}/api/v1/ml-lison-data`, {
      inputData: payload,
    });

    return resp.data;
  } catch (err) {
    return ("error EZDA API call", err);
  }
}
export async function Submit_Lison_annotations(
  dicom_data,
  stack_id,
  SaveInputLisonData,
  mlDataUpdated,
) {
  var annotations = get_annotations(
    dicom_data.dicom_data,
    dicom_data.drawingTools,
  );

  var stack_ids = Object.keys(annotations);
  let etdrsToolData = null;
  if (annotations[stack_ids[0]]?.OP["Etdrs"]) {
    etdrsToolData =
      annotations[stack_ids[0]]?.OP["Etdrs"]?.data[0]?.handles.start;
  }

  const opData = dicom_data.dicom_data[stack_id];
  let lisonLines = extractPointsCoordinates(annotations[stack_ids[0]].OPT);

  let starting_points = [];
  let ending_points = [];
  opPoints(lisonLines, opData, starting_points, ending_points);
  let opMetaData = dicom_data.opMetaData;

  // let opMetaData = await opMetadataFetch(opData.OP.url);

  let ir_bscan_distance_ = veriticalMaxDistance(opData.OPT.metadata.frames);
  // const foundObject = opMetaData.find(obj => obj.name === "Columns");

  let pixel_spacing = await ensureOpPixelSpacing(opMetaData, opData);

  let ir_image_size = getOpImageSize(opMetaData, opData);
  const xCoordinates = opData?.OPT?.metadata?.rect_poly.map(
    (vertex) => vertex[0],
  );
  const yCoordinates = opData?.OPT?.metadata?.rect_poly.map(
    (vertex) => vertex[1],
  );

  // Calculate center coordinates

  // here adding the centre coordinates of etdrs circles

  // const etdrsToolState = cornerstoneTools.getToolState(dcmRef.current, "Etdrs");
  const centerX = xCoordinates.reduce((sum, x) => sum + x, 0) / 4;
  const centerY = yCoordinates.reduce((sum, y) => sum + y, 0) / 4;
  let finalData = {
    ir_ez_lines: [
      {
        start_points: starting_points,
        end_points: ending_points,
      },
    ],
    ir_scale_x: pixel_spacing[0],
    ir_scale_y: pixel_spacing[1],
    ir_bscan_distance_px: ir_bscan_distance_,
    ir_image_size: ir_image_size,
    etdrs_center: [
      parseInt(etdrsToolData ? etdrsToolData.x : centerX),
      parseInt(etdrsToolData ? etdrsToolData.y : centerY),
    ],
    bscan_rectangle_corners: opData?.OPT?.metadata?.rect_poly,
  };

  let mlAPIPayLoad = [];
  mlAPIPayLoad.push(finalData);

  let receivedData = await fetchMlLisonData(mlAPIPayLoad);

  mlDataUpdated(mlAPIPayLoad, receivedData);
  //here have to add check for successful api call and success response only then call this function
  SaveInputLisonData(lisonLines);

  return receivedData;
}
export async function sendFileUpload(
  id,
  file,
  progressCall,
  dataQuestion,
  updateState,
) {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("id", id);
  try {
    const response = await axios.post(
      `${API_URL}/api/v1/file-upload`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total,
          );

          var progressBar = document.getElementById(
            dataQuestion + "_" + file.name + "progressBar",
          );
          var progressBarContainer = document.getElementById(
            dataQuestion + "_" + file.name + "progressBarContainer",
          );

          // Change the HTML content of the div
          if (progressBar && progressBarContainer) {
            progressBar.textContent = `${percentCompleted}%`; // Update the displayed percentage
            progressBar.style.width = `${percentCompleted}%`;

            if (percentCompleted === 100) {
              // progressBar.style.display = "none";

              progressBarContainer.style.display = "none";
            } else {
              // progressBarContainer.style.display = "block";
              // progressBar.style.display = "block";
            }
          }

          // progressCall(dataQuestion, percentCompleted);

          // if (onUploadProgress) {
          //   onUploadProgress((prev) => ({
          //     ...prev,
          //     progressKey: percentCompleted,
          //   }));
          // }
        },
      },
    );
    if (!dataQuestion) {
      return response.data;
    }

    let imgElement = document.getElementById(dataQuestion + "_" + file.name);

    // Replace the src attribute with the new URL
    if (imgElement) {
      imgElement.src = response.data.urls[0];
    }

    updateState(dataQuestion + "_" + file.name, response.data.urls[0]);

    return;
  } catch (error) {
    console.error("Error uploading file:", error);
  }
}

/** Delay before IR capture so compute-volumes overlays finish rendering. */
export const IR_SCREENSHOT_CAPTURE_DELAY_MS = 3000;

/**
 * Capture the IR/OP viewer DOM, upload PNG, and store URL for form attachment.
 */
export async function captureIrScreenshot(
  dicom_data,
  stack_id,
  dispatch,
  { delayMs = IR_SCREENSHOT_CAPTURE_DELAY_MS } = {},
) {
  const latestDcm = store.getState().dcm;
  const opRef =
    latestDcm?.OpRef?.current || dicom_data?.OpRef?.current || null;
  if (!opRef || !stack_id) {
    return null;
  }

  if (delayMs > 0) {
    await new Promise((resolve) => setTimeout(resolve, delayMs));
  }

  try {
    const dataUrl = await toPng(opRef, {
      skipFonts: true,
      cacheBust: true,
    });
    const blob = await (await fetch(dataUrl)).blob();
    const randomNumber = Math.floor(1000 + Math.random() * 9000);
    const { study_code, subject_code, visit_label } =
      latestDcm?.basicMetaData || dicom_data?.basicMetaData || {};

    const file = new File(
      [blob],
      `${study_code}_${subject_code}_${visit_label}_${randomNumber}.png`,
      { type: blob.type },
    );

    const responseFileUpload = await sendFileUpload(stack_id, file);
    const screenshotUrl = responseFileUpload?.urls?.[0];

    if (screenshotUrl) {
      dispatch({
        type: "mainScreenShot_url_changed",
        mainScreenShotUrl: screenshotUrl,
      });
      dispatch({
        type: "submitDisabled_changed",
        submitDisable: false,
      });
    }

    return screenshotUrl;
  } catch (error) {
    console.error("Failed to capture IR screenshot:", error);
    return null;
  }
}

export function getOpCornerstoneElement(dicom_data) {
  return dicom_data?.OpCornerstoneRef?.current || null;
}

export function applyEtdrsVolumesToOpElement(opEl, volumesData) {
  if (!opEl || volumesData == null) return false;
  try {
    const etdrsState = cornerstoneTools.getToolState(opEl, "Etdrs");
    if (!etdrsState?.data?.length) return false;
    etdrsState.data.forEach((data) => {
      data.volumesData = volumesData;
    });
    return true;
  } catch (_e) {
    return false;
  }
}

function applyEtdrsVolumesToRedux(stack_id, volumesData, dicom_data) {
  if (!stack_id || volumesData == null) return;
  const existing =
    dicom_data?.dicom_data?.[stack_id]?.OP?.annotations?.Etdrs ||
    store.getState().dcm?.dicom_data?.[stack_id]?.OP?.annotations?.Etdrs;
  if (!existing?.data?.length) return;

  const patchedEtdrs = {
    ...existing,
    data: existing.data.map((entry) => ({
      ...entry,
      volumesData,
    })),
  };

  const currentAnnotations =
    store.getState().dcm?.dicom_data?.[stack_id]?.OP?.annotations || {};
  store.dispatch({
    type: "SET_ANNOTATION_DATA",
    payload: { ...currentAnnotations, Etdrs: patchedEtdrs },
    id: stack_id,
    key: "OP",
  });
}

/** Copy live OP cornerstone tool state (incl. ETDRS volumes) into redux before save. */
export function syncOpAnnotationsFromElement(dicom_data, stack_id) {
  if (!stack_id) {
    return store.getState().dcm;
  }

  const opEl = getOpCornerstoneElement(dicom_data);
  const existing = dicom_data?.dicom_data?.[stack_id]?.OP?.annotations || {};
  const payload = { ...existing };

  if (opEl) {
    const tools = [...(dicom_data.drawingTools || []), "Etdrs"];
    tools.forEach((tool) => {
      try {
        const toolState = cornerstoneTools.getToolState(opEl, tool);
        if (toolState) payload[tool] = toolState;
      } catch (_e) {
        /* noop */
      }
    });
  }

  store.dispatch({
    type: "SET_ANNOTATION_DATA",
    payload,
    id: stack_id,
    key: "OP",
  });

  return store.getState().dcm;
}

export function prepareDicomDataForAnnotationSave(
  dicom_data,
  stack_id,
  volumesData = null,
) {
  const opEl = getOpCornerstoneElement(dicom_data);
  if (volumesData != null) {
    const appliedOnElement = applyEtdrsVolumesToOpElement(opEl, volumesData);
    if (opEl && appliedOnElement) {
      try {
        cornerstone.updateImage(opEl);
      } catch (_e) {
        /* noop */
      }
    }
    if (!appliedOnElement) {
      applyEtdrsVolumesToRedux(stack_id, volumesData, dicom_data);
    }
  }
  return syncOpAnnotationsFromElement(
    volumesData != null ? store.getState().dcm : dicom_data,
    stack_id,
  );
}

export function processAnnotations(dicomData, stack_id = null) {
  var annotations = get_annotations(
    dicomData.dicom_data,
    dicomData.drawingTools,
  );

  var stack_ids = Object.keys(annotations);
  if (!stack_ids.length) {
    return { OP: {}, OPT: {} };
  }

  const stackKey = stack_id && annotations[stack_id] ? stack_id : stack_ids[0];
  const stackEntry = annotations[stackKey];
  if (!stackEntry?.OP) {
    return stackEntry || { OP: {}, OPT: {} };
  }

  const etdrsEntry = stackEntry.OP.Etdrs;
  if (
    !dicomData?.etdrsActive &&
    etdrsEntry &&
    !hasEtdrsVolumesData(etdrsEntry)
  ) {
    delete stackEntry.OP.Etdrs;
  }

  if (stackEntry.OP.MultiPointProbe) {
    delete stackEntry.OP.MultiPointProbe;
  }

  if (stackEntry.OP.MultiLine) {
    delete stackEntry.OP.MultiLine;
  }

  if (dicomData?.lisonsAreasData && dicomData?.LisonActive) {
    stackEntry.OP["lisonsAreasData"] = dicomData?.lisonsAreasData;
  }

  delete stackEntry.annots;
  return stackEntry;
}

export function buildSerializedAnnotationsForSave(dicomData, stack_id = null) {
  const stackEntry = processAnnotations(dicomData, stack_id);
  if (stackEntry?.OPT) {
    mergeJoinedSegmentLinesIntoAiSegmentation(stackEntry.OPT);
  }
  return serializeAnnotationsForSave(stackEntry);
}

function mergeUniqueSegmentationPoints(points, pointsToAdd) {
  const seen = new Set();
  const merged = [];

  [...(points || []), ...(pointsToAdd || [])].forEach((point) => {
    if (point?.x == null || point?.y == null) {
      return;
    }

    const normalizedPoint = {
      x: Math.round(point.x),
      y: Math.round(point.y),
    };
    const key = `${normalizedPoint.x},${normalizedPoint.y}`;

    if (!seen.has(key)) {
      seen.add(key);
      merged.push(normalizedPoint);
    }
  });

  return merged.sort((a, b) => a.x - b.x);
}

export function mergeJoinedSegmentLinesIntoAiSegmentation(optAnnotations) {
  if (!optAnnotations || typeof optAnnotations !== "object") {
    return false;
  }

  let changed = false;

  Object.keys(optAnnotations).forEach((url) => {
    const urlData = optAnnotations[url];
    const lineData = urlData?.Line?.data;
    const aiData = urlData?.AiSegmentation?.data;

    if (!Array.isArray(lineData) || !Array.isArray(aiData)) {
      return;
    }

    lineData.forEach((item) => {
      if (item?.name !== "Segment") {
        return;
      }

      const curvePixels = extractSegmentCurvePixels(item);
      const matchingObj = aiData.find((obj) => obj?.label === item?.label);

      if (
        !matchingObj ||
        !Array.isArray(curvePixels) ||
        curvePixels.length === 0
      ) {
        return;
      }

      matchingObj.points = mergeUniqueSegmentationPoints(
        matchingObj.points,
        curvePixels,
      );
      matchingObj.invalidated = true;
      changed = true;
    });
  });

  return changed;
}

export async function saveUserSettings(user_id, settings) {
  const resp = await axios.post(
    `${API_URL}/api/v1/dicom/user-settings?user_id=${user_id}`,
    {
      user_id: user_id,
      preferences: {
        oct_viewer_preferences: {
          ...settings,
        },
      },
    },
  );
  return resp.data;
}
export function buildVolumesSaveParams(query) {
  if (!query?.get) return null;
  const stackId = query.get("stack_id");
  if (!stackId) return null;
  return {
    user_id: query.get("user_id"),
    series_number: query.get("series_no"),
    role: query.get("role"),
    formType: query.get("form_version_id") ? "additional_form" : "primary_form",
    form_version_id: query.get("form_version_id") || "",
  };
}

export function hasEtdrsVolumesData(etdrsAnnotation) {
  return hasCompactOrLegacyEtdrsVolumesData(etdrsAnnotation);
}

export function getSavedEtdrsAnnotation(propsAnnotations, dicom_data, stackId) {
  return (
    propsAnnotations?.Etdrs ||
    dicom_data?.dicom_data?.[stackId]?.OP?.annotations?.Etdrs ||
    null
  );
}

/** True when initial load should call compute-volumes (no saved ETDRS volumes yet). */
export function shouldCallInitialComputeVolumes(
  propsAnnotations,
  dicom_data,
  stackId,
  { role = null, adjudicatorUserId = null } = {},
) {
  if (!dicom_data?.fluidSegmentation) return false;
  if (dicom_data?.etdrsVolumesFromSegmentationApi) return false;
  if (!isOpManufacturerMetadataReady(dicom_data?.opMetaData)) return false;

  if (role === "adjudicator" && adjudicatorUserId != null) {
    const activeSourceId = String(dicom_data?.fetchedId ?? adjudicatorUserId);
    if (activeSourceId !== String(adjudicatorUserId)) {
      return false;
    }
  }

  const savedEtdrs = getSavedEtdrsAnnotation(
    propsAnnotations,
    dicom_data,
    stackId,
  );
  if (hasEtdrsVolumesData(savedEtdrs)) return false;

  return true;
}

export async function saveAnnotationsAfterVolumes(
  dicom_data,
  _dispatch,
  saveParams,
  volumesData = null,
) {
  if (!saveParams?.stack_id) return;

  const dataForSave = prepareDicomDataForAnnotationSave(
    dicom_data,
    saveParams.stack_id,
    volumesData,
  );

  await save_annotations(
    dataForSave,
    saveParams.stack_id,
    saveParams.user_id,
    saveParams.series_number,
    saveParams.role,
    saveParams.formType,
    saveParams.form_version_id,
  );
}

export function buildAnnotationSaveRequestPayload({
  stack_id,
  user_id,
  series_number,
  role,
  formType,
  form_version_id,
  adjudication_id,
  dicom_data_rest_annotation,
  annotations,
}) {
  if (role === "adjudicator") {
    return {
      stack_id,
      user_id,
      annotations,
      adjudication_id,
      form_type: formType,
      form_version_id,
    };
  }

  return {
    stack_id,
    annotations,
    form_type: formType,
    form_version_id,
    mt: {
      stack_id,
      user_id,
      series_number,
      dicom_data_rest_annotation: dicom_data_rest_annotation ?? {},
    },
  };
}

export function getAnnotationSaveCacheKey(
  stack_id,
  role,
  formType,
  form_version_id,
  user_id,
) {
  return [stack_id, role, formType, form_version_id || "", user_id || ""].join(
    "|",
  );
}

function stableStringifyForCompare(value) {
  if (value === null || typeof value !== "object") {
    return JSON.stringify(value);
  }

  if (Array.isArray(value)) {
    return `[${value.map(stableStringifyForCompare).join(",")}]`;
  }

  const keys = Object.keys(value).sort();
  return `{${keys
    .map(
      (key) =>
        `${JSON.stringify(key)}:${stableStringifyForCompare(value[key])}`,
    )
    .join(",")}}`;
}

export function isSameAnnotationSavePayload(cached, cacheKey, requestPayload) {
  if (!cached?.payload || cached.cacheKey !== cacheKey) {
    return false;
  }

  return (
    stableStringifyForCompare(cached.payload) ===
    stableStringifyForCompare(requestPayload)
  );
}

export function isSameAsLastSuccessfulAnnotationSave(
  lastSuccessfulSave,
  cacheKey,
  requestPayload,
) {
  return isSameAnnotationSavePayload(
    lastSuccessfulSave,
    cacheKey,
    requestPayload,
  );
}

function storeLastSuccessfulAnnotationSave(cacheKey, payload) {
  store.dispatch({
    type: "SET_LAST_SUCCESSFUL_ANNOTATION_SAVE",
    cacheKey,
    payload,
  });
}

function setPendingAnnotationSavePayload(cacheKey, payload) {
  store.dispatch({
    type: "SET_PENDING_ANNOTATION_SAVE_PAYLOAD",
    cacheKey,
    payload,
  });
}

function clearPendingAnnotationSavePayload() {
  store.dispatch({
    type: "SET_PENDING_ANNOTATION_SAVE_PAYLOAD",
    cacheKey: null,
    payload: null,
  });
}

async function executeAnnotationSaveRequest({
  requestPayload,
  cacheKey,
  role,
  onRequestInitiated,
  requestSignal,
  generation,
}) {
  try {
    updateAnnotationSaveProgress(generation, {
      percent: 3,
      etaSeconds: null,
      message: "Preparing annotations...",
    });

    onRequestInitiated?.();

    updateAnnotationSaveProgress(generation, {
      percent: 5,
      etaSeconds: null,
      message: "Uploading annotations...",
    });

    const onUploadProgress =
      createAnnotationSaveUploadProgressHandler(generation);
    const axiosConfig = { signal: requestSignal, onUploadProgress };

    if (role === "adjudicator") {
      await axios.post(
        `${API_URL}/api/v1/qa-form-adjudicator`,
        requestPayload,
        axiosConfig,
      );
    } else {
      await axios.post(
        `${API_URL}/api/v1/file/save-annotations`,
        requestPayload,
        axiosConfig,
      );
    }

    storeLastSuccessfulAnnotationSave(cacheKey, requestPayload);

    updateAnnotationSaveProgress(generation, {
      percent: 100,
      etaSeconds: 0,
      message: "Save complete",
    });
  } catch (err) {
    if (isAnnotationSaveAborted(err)) return;
    console.error("Save annotations failed:", err);
    throw err;
  } finally {
    finishAnnotationSave(generation);
    if (generation === pendingAnnotationSave.generation) {
      clearPendingAnnotationSavePayload();
      pendingAnnotationSave.promise = null;
    }
  }
}

export async function save_annotations(
  dicom_data,
  stack_id,
  user_id,
  series_number,
  role,
  formType,
  form_version_id,
  options = {},
) {
  const { onRequestInitiated, signal: externalSignal } = options;

  const annotations = buildSerializedAnnotationsForSave(dicom_data, stack_id);
  const requestPayload = buildAnnotationSaveRequestPayload({
    stack_id,
    user_id,
    series_number,
    role,
    formType,
    form_version_id,
    adjudication_id: dicom_data.adjudication_id,
    dicom_data_rest_annotation: dicom_data.dicom_data_rest_annotation,
    annotations,
  });
  const cacheKey = getAnnotationSaveCacheKey(
    stack_id,
    role,
    formType,
    form_version_id,
    user_id,
  );
  const {
    lastSuccessfulAnnotationSave,
    pendingAnnotationSavePayload,
    annotationSaveInProgress,
  } = store.getState().dcm;

  if (
    isSameAnnotationSavePayload(
      lastSuccessfulAnnotationSave,
      cacheKey,
      requestPayload,
    )
  ) {
    onRequestInitiated?.();
    return;
  }

  if (
    annotationSaveInProgress &&
    pendingAnnotationSave.promise &&
    isSameAnnotationSavePayload(
      pendingAnnotationSavePayload,
      cacheKey,
      requestPayload,
    )
  ) {
    onRequestInitiated?.();
    return pendingAnnotationSave.promise;
  }

  if (annotationSaveInProgress && pendingAnnotationSave.controller) {
    abortPendingAnnotationSave();
  }

  setPendingAnnotationSavePayload(cacheKey, requestPayload);

  const { controller, generation } = beginAnnotationSave();
  const requestSignal = externalSignal || controller.signal;

  const savePromise = executeAnnotationSaveRequest({
    requestPayload,
    cacheKey,
    role,
    onRequestInitiated,
    requestSignal,
    generation,
  });

  pendingAnnotationSave.promise = savePromise;

  return savePromise;
}

export async function generateMlPayload(dicom_data, stack_id) {
  // Extract annotations and required data
  var annotations = get_annotations(
    dicom_data.dicom_data,
    dicom_data.drawingTools,
  );

  var stack_ids = Object.keys(annotations);
  let etdrsToolData =
    annotations[stack_ids[0]]?.OP["Etdrs"]?.data[0]?.handles.start || null;

  const opData = dicom_data.dicom_data[stack_id];
  let lisonLines = extractPointsCoordinates(annotations[stack_ids[0]].OPT);

  // Fetch metadata
  let opMetaData = dicom_data.opMetaData;

  // Calculate points
  let starting_points = [];
  let ending_points = [];
  opPoints(lisonLines, opData, starting_points, ending_points);

  let ir_bscan_distance_ = veriticalMaxDistance(opData.OPT.metadata.frames);

  let pixel_spacing = await ensureOpPixelSpacing(opMetaData, opData);

  let ir_image_size = getOpImageSize(opMetaData, opData);

  const xCoordinates = opData?.OPT?.metadata?.rect_poly.map(
    (vertex) => vertex[0],
  );
  const yCoordinates = opData?.OPT?.metadata?.rect_poly.map(
    (vertex) => vertex[1],
  );

  const centerX = xCoordinates.reduce((sum, x) => sum + x, 0) / 4;
  const centerY = yCoordinates.reduce((sum, y) => sum + y, 0) / 4;

  let finalData = {
    ir_ez_lines: [
      {
        start_points: starting_points,
        end_points: ending_points,
      },
    ],
    ir_scale_x: pixel_spacing[0],
    ir_scale_y: pixel_spacing[1],
    ir_bscan_distance_px: ir_bscan_distance_,
    ir_image_size: ir_image_size,
    etdrs_center: [
      parseInt(etdrsToolData ? etdrsToolData.x : centerX),
      parseInt(etdrsToolData ? etdrsToolData.y : centerY),
    ],
    bscan_rectangle_corners: opData?.OPT?.metadata?.rect_poly,
  };

  return [finalData];
}

// Calculate bezier curve gradient - same as in drawJoinedLines.js
function gradient(a, b) {
  return (b.y - a.y) / (b.x - a.x);
}

// Function to extract all pixels along a bezier curve for Segment objects
export function extractSegmentCurvePixels(segmentObject) {
  if (segmentObject.name !== "Segment") {
    return null;
  }

  const points = segmentObject.handles.points;
  if (!points || points.length < 2) {
    return [];
  }

  // Parameters matching those in bzCurve function
  const f = 0.2; // Curve factor
  const t = 0.7; // Smoothness control

  const allCurvePoints = [];
  const pointsForCurve = points.map((p) => ({ x: p.x, y: p.y }));

  // Generate points along each curve segment
  for (let i = 0; i < pointsForCurve.length - 1; i++) {
    const startPoint = pointsForCurve[i];
    const endPoint = pointsForCurve[i + 1];

    // Generate control points for this segment
    let dx1 = 0,
      dy1 = 0;

    // Calculate control points
    if (i > 0) {
      const prevPoint = pointsForCurve[i - 1];
      const m = gradient(prevPoint, endPoint);
      dx1 = (endPoint.x - startPoint.x) * -f;
      dy1 = dx1 * m * t;
    }

    let dx2 = 0,
      dy2 = 0;
    if (i < pointsForCurve.length - 2) {
      const nextPoint = pointsForCurve[i + 2];
      const m = gradient(startPoint, nextPoint);
      dx2 = (nextPoint.x - endPoint.x) * -f;
      dy2 = dx2 * m * t;
    }

    // Dynamically determine steps based on segment length for ~1 pixel spacing
    const segmentLength = Math.sqrt(
      Math.pow(endPoint.x - startPoint.x, 2) +
        Math.pow(endPoint.y - startPoint.y, 2),
    );
    const steps = Math.max(1, Math.ceil(segmentLength)); // At least 1 step

    for (let step = 0; step <= steps; step++) {
      const progress = step / steps;

      // Cubic bezier formula: P = (1-t)³P₀ + 3(1-t)²tP₁ + 3(1-t)t²P₂ + t³P₃
      const cp1x = startPoint.x - dx1;
      const cp1y = startPoint.y - dy1;
      const cp2x = endPoint.x + dx2;
      const cp2y = endPoint.y + dy2;

      const t1 = 1 - progress;
      const t2 = progress;

      const x =
        Math.pow(t1, 3) * startPoint.x +
        3 * Math.pow(t1, 2) * t2 * cp1x +
        3 * t1 * Math.pow(t2, 2) * cp2x +
        Math.pow(t2, 3) * endPoint.x;

      const y =
        Math.pow(t1, 3) * startPoint.y +
        3 * Math.pow(t1, 2) * t2 * cp1y +
        3 * t1 * Math.pow(t2, 2) * cp2y +
        Math.pow(t2, 3) * endPoint.y;

      allCurvePoints.push({ x: Math.round(x), y: Math.round(y) });
    }
  }

  // Remove duplicates
  const uniquePoints = [];
  const seen = new Set();

  allCurvePoints.forEach((point) => {
    const key = `${point.x},${point.y}`;
    if (!seen.has(key)) {
      seen.add(key);
      uniquePoints.push(point);
    }
  });

  return uniquePoints;
}

// Function to extract all Segment objects and their curve pixels
export function extractAllSegmentCurves(annotations) {
  const allSegmentCurves = [];

  // Process the OPT annotations from each frame/url
  if (annotations && annotations.OPT) {
    Object.keys(annotations.OPT).forEach((url) => {
      const urlData = annotations.OPT[url];

      // Check all tools in this URL entry
      Object.keys(urlData).forEach((toolName) => {
        const toolData = urlData[toolName];

        // Look for Segment objects in data arrays
        if (toolData.data && Array.isArray(toolData.data)) {
          toolData.data.forEach((item) => {
            if (item.name === "Segment") {
              const curvePixels = extractSegmentCurvePixels(item);
              if (curvePixels && curvePixels.length > 0) {
                allSegmentCurves.push({
                  frameUrl: url,
                  toolName,
                  segmentId: item.uuid,
                  curvePixels,
                });
              }
            }
          });
        }
      });
    });
  }

  return allSegmentCurves;
}

// Helper function to get segment curve pixels from current dicom data
export function getSegmentCurvePixels(dicom_data) {
  if (!dicom_data || !dicom_data.dicom_data) {
    return [];
  }

  // Get annotations from dicom data
  const annotations = get_annotations(
    dicom_data.dicom_data,
    dicom_data.drawingTools,
  );

  // Process each stack
  const result = {};
  Object.keys(annotations).forEach((stackId) => {
    const stackData = annotations[stackId];

    // Extract segment curves for this stack
    const segmentCurves = extractAllSegmentCurves(stackData);
    if (segmentCurves.length > 0) {
      result[stackId] = segmentCurves;
    }
  });

  return result;
}
export const extractAiSegmentationData = (
  dicom_data,
  drawingTools,
  opMetaData,
  optMetaData,
  stack_id,
  isFluidSegmentation,
) => {
  const result = {};
  let etdrsToolData = null;
  if (dicom_data?.dicom_data[stack_id]?.OP.annotations?.["Etdrs"]) {
    etdrsToolData =
      dicom_data?.dicom_data[stack_id]?.OP.annotations?.["Etdrs"]?.data[0]
        ?.handles.start;
  }
  const opData = dicom_data?.dicom_data[stack_id];
  const imgIds = opData?.OPT?.stack?.imageIds;

  const optMetadata = dicom_data?.dicom_data[stack_id]?.OPT?.metadata;

  let IRmeta = dicom_data?.dicom_data[stack_id]?.OP?.metadata;

  result.rectangle_coordinates = optMetadata?.rect_poly || [];
  result.ir_image_size = [IRmeta?.width || 0, IRmeta?.height || 0];
  result.scale_x_ir = IRmeta?.pixel_spacing?.columnPixelSpacing || 0;
  result.scale_y_bscan = optMetadata?.pixel_spacing?.rowPixelSpacing || 0;

  logMetadataComparison(optMetadata, optMetaData, "extractAiSegmentationData");
  let centerX = 0,
    centerY = 0;
  if (optMetadata?.rect_poly && optMetadata.rect_poly.length === 4) {
    // Calculate the average of all 4 corner points
    const totalX = optMetadata.rect_poly.reduce(
      (sum, point) => sum + point[0],
      0,
    );
    const totalY = optMetadata.rect_poly.reduce(
      (sum, point) => sum + point[1],
      0,
    );
    centerX = Math.round(totalX / 4);
    centerY = Math.round(totalY / 4);
  }

  result.etdrs_center = [
    parseInt(etdrsToolData?.x) || centerX,
    parseInt(etdrsToolData?.y) || centerY,
  ];

  if (isFluidSegmentation) {
    return result;
  }
  const segmentationData = {};
  const annotations = get_annotations(dicom_data.dicom_data, drawingTools);

  // Extract segmentation data
  if (annotations) {
    const stackIds = Object.keys(annotations);

    if (stackIds.length > 0 && annotations[stackIds[0]].OPT) {
      const optData = annotations[stackIds[0]].OPT;
      mergeJoinedSegmentLinesIntoAiSegmentation(optData);
      let frameIndex = 1;

      Object.keys(optData).forEach((url) => {
        const urlData = optData[url];

        if (urlData.AiSegmentation) {
          const frameKey = `frame${frameIndex}`;
          segmentationData[frameKey] = urlData.AiSegmentation;
          frameIndex++;
        }
      });
    }
  }

  if (Object.keys(segmentationData).length === 0) {
    let frameIndex = 1;
    imgIds?.forEach((imageId) => {
      const arrayOfObjects = getAiSegmentationData(
        dicom_data?.aiSegmentationData,
        imageId,
        dicom_data?.settings?.Segmentation,
      );

      const frameKey = `frame${frameIndex}`;
      segmentationData[frameKey] = { data: arrayOfObjects };
      frameIndex++;
    });
  }
  // Get OPT metadata for rectangle coordinates and image size

  // Calculate center point from rectangle coordinates
  // Default values as specified

  // Add pixel spacing metadata if available

  result.segmentation = segmentationData;

  return result;
};

export const Etdrs_Segmentation_Api_Call = async (
  dicom_data,
  stack_id,
  dispatch,
  center,
) => {
  const etdrsValuesFromSegmentationApi = extractAiSegmentationData(
    dicom_data,
    dicom_data?.drawingTools,
    dicom_data?.opMetaData,
    dicom_data?.optMetaData,
    stack_id,
    false,
  );
  if (center?.x && center?.y) {
    etdrsValuesFromSegmentationApi.etdrs_center = [
      parseInt(center?.x),
      parseInt(center?.y),
    ];
  }

  try {
    const response = await axios.post(
      `${API_URL}/api/v1/compute-etdrs`,
      etdrsValuesFromSegmentationApi,
      {
        headers: {
          "Content-Type": "application/json",
        },
      },
    );

    if (response.status === 200) {
      dispatch({
        type: "SET_ETDRS_VALUES_FROM_SEGMENTATION_API",
        edtrsValuesFromSegmentationApi: response.data,
      });
      return response.data;
    }
  } catch (error) {
    console.error("Error calling compute_etdrs API:", error);
    if (error.response) {
      console.error("Error response:", error.response.data);
      console.error("Error status:", error.response.status);
    }
    throw error;
  }
};
function resolveFluidVolumeId(stackData, fluidSegmentation, imageIds) {
  const fromFluid =
    fluidSegmentation?.volume_id ||
    fluidSegmentation?.result?.volume_id ||
    null;
  if (fromFluid) return fromFluid;

  const optUrl = stackData?.OPT?.url || stackData?.OPT?.metadata?.url || "";
  const fileBase = String(optUrl)
    .split("/")
    .pop()
    ?.replace(/\.dcm$/i, "");
  if (fileBase) {
    return fileBase.endsWith("_opt") ? fileBase : `${fileBase}_opt`;
  }

  const firstImageId = imageIds?.[0] || "";
  const wadouriBase = String(firstImageId)
    .replace(/^wadouri:/i, "")
    .split("/")
    .pop()
    ?.replace(/\.dcm$/i, "");
  if (wadouriBase) {
    return wadouriBase.endsWith("_opt") ? wadouriBase : `${wadouriBase}_opt`;
  }

  return null;
}

/**
 * Order rectangle corners for compute-volumes APIs:
 * top left → top right → bottom right → bottom left (image coords, y increases down).
 */
function orderRectangleCoordinatesForVolumesApi(corners) {
  if (!Array.isArray(corners) || corners.length !== 4) {
    return corners || [];
  }

  const points = corners.map((p) => [Number(p[0]), Number(p[1])]);
  const sortedByY = [...points].sort((a, b) => a[1] - b[1] || a[0] - b[0]);
  const topTwo = sortedByY.slice(0, 2);
  const bottomTwo = sortedByY.slice(2, 4);

  const topRight = topTwo[0][0] >= topTwo[1][0] ? topTwo[0] : topTwo[1];
  const topLeft = topTwo[0][0] >= topTwo[1][0] ? topTwo[1] : topTwo[0];
  const bottomRight =
    bottomTwo[0][0] >= bottomTwo[1][0] ? bottomTwo[0] : bottomTwo[1];
  const bottomLeft =
    bottomTwo[0][0] >= bottomTwo[1][0] ? bottomTwo[1] : bottomTwo[0];

  return [topLeft, topRight, bottomRight, bottomLeft];
}

function collectFluidVolumesPayloadInputs(dicom_data, stack_id, center) {
  const stackData = dicom_data?.dicom_data?.[stack_id];
  const optMetadata = stackData?.OPT?.metadata;
  const opMetadata = stackData?.OP?.metadata;
  const imageIds = stackData?.OPT?.stack?.imageIds || [];

  let etdrsToolData =
    stackData?.OP?.annotations?.Etdrs?.data?.[0]?.handles?.start;
  const opEl = getOpCornerstoneElement(dicom_data);
  if (!etdrsToolData?.x && opEl) {
    try {
      const etdrsState = cornerstoneTools.getToolState(opEl, "Etdrs");
      etdrsToolData = etdrsState?.data?.[0]?.handles?.start;
    } catch (_e) {
      /* noop */
    }
  }

  const rectangle_coordinates = orderRectangleCoordinatesForVolumesApi(
    optMetadata?.rect_poly || [],
  );

  let centerX = 0;
  let centerY = 0;
  if (rectangle_coordinates.length === 4) {
    centerX = Math.round(
      rectangle_coordinates.reduce((sum, p) => sum + p[0], 0) / 4,
    );
    centerY = Math.round(
      rectangle_coordinates.reduce((sum, p) => sum + p[1], 0) / 4,
    );
  }

  const manufacturer = getOpManufacturer(dicom_data?.opMetaData);
  const fluidSegmentation = dicom_data?.fluidSegmentation;
  const volumePayloadLabels = getFluidLabelsForVolumesApi(manufacturer);
  const bscans = buildFluidBscansForVolumesApi(
    imageIds,
    fluidSegmentation,
    volumePayloadLabels,
  );
  const volumeId = resolveFluidVolumeId(stackData, fluidSegmentation, imageIds);
  const apiVersion =
    fluidSegmentation?.result?.API_version ??
    fluidSegmentation?.API_version ??
    0.1;

  return {
    stackData,
    optMetadata,
    opMetadata,
    imageIds,
    rectangle_coordinates,
    etdrs_center: [
      parseInt(center?.x ?? etdrsToolData?.x ?? centerX, 10),
      parseInt(center?.y ?? etdrsToolData?.y ?? centerY, 10),
    ],
    manufacturer,
    fluidSegmentation,
    bscans,
    volumeId,
    apiVersion,
    totalBscans:
      fluidSegmentation?.total_bscans ||
      fluidSegmentation?.result?.bscans?.length ||
      imageIds.length,
  };
}

/** Topcon: mat_compute_volumes (`fda-payload-working.json` shape). */
function buildTopconFluidVolumesPayload(inputs) {
  const opPs = inputs.opMetadata?.pixel_spacing || {};
  const optPs = inputs.optMetadata?.pixel_spacing || {};

  return {
    rectangle_coordinates: inputs.rectangle_coordinates,
    fundus_image_size: [
      inputs.opMetadata?.width || 0,
      inputs.opMetadata?.height || 0,
    ],
    etdrs_center: inputs.etdrs_center,
    scale_x_cf: opPs.columnPixelSpacing || 0,
    scale_y_cf: opPs.rowPixelSpacing || 0,
    scale_x_bscan: optPs.columnPixelSpacing || 0,
    scale_y_bscan: optPs.rowPixelSpacing || 0,
    status: inputs.fluidSegmentation?.status || "success",
    volume_id: inputs.volumeId,
    total_bscans: inputs.totalBscans,
    result: {
      API_version: inputs.apiVersion,
      volume_id: inputs.volumeId,
      bscans: inputs.bscans,
    },
  };
}

/**
 * Non-Topcon RFQ: `spectrailis-rfq-payload.json` shape.
 * scale_x_ir / scale_y_bscan match extractAiSegmentationData (compute_etdrs / EZDA).
 */
function buildRfqFluidVolumesPayload(inputs) {
  const opPs = inputs.opMetadata?.pixel_spacing || {};
  const optPs = inputs.optMetadata?.pixel_spacing || {};

  return {
    rectangle_coordinates: inputs.rectangle_coordinates,
    ir_image_size: [
      inputs.opMetadata?.width || 0,
      inputs.opMetadata?.height || 0,
    ],
    etdrs_center: inputs.etdrs_center,
    scale_x_ir: opPs.columnPixelSpacing || 0,
    scale_y_bscan: optPs.rowPixelSpacing || 0,
    API_version: inputs.apiVersion,
    bscans: inputs.bscans,
    volume_id: inputs.volumeId,
  };
}

/** Build compute-volumes payload — Topcon vs RFQ format based on manufacturer. */
export function buildFluidVolumesPayload(dicom_data, stack_id, center) {
  const inputs = collectFluidVolumesPayloadInputs(dicom_data, stack_id, center);
  if (isTopconManufacturer(inputs.manufacturer)) {
    return buildTopconFluidVolumesPayload(inputs);
  }
  return buildRfqFluidVolumesPayload(inputs);
}

export const Etdrs_Volumes_Api_Call = async (
  dicom_data,
  stack_id,
  dispatch,
  center,
  saveParams = null,
) => {
  let activeDicomData = store.getState().dcm || dicom_data;
  if (!isOpManufacturerMetadataReady(activeDicomData?.opMetaData)) {
    await waitForOpManufacturerMetadata();
    activeDicomData = store.getState().dcm || dicom_data;
  }
  if (!isOpManufacturerMetadataReady(activeDicomData?.opMetaData)) {
    throw new Error("OP manufacturer metadata not available");
  }

  const etdrsVolumesApiPayload = buildFluidVolumesPayload(
    activeDicomData,
    stack_id,
    center,
  );

  const manufacturer = getOpManufacturer(activeDicomData?.opMetaData);
  const useTopconVolumesApi = isTopconManufacturer(manufacturer);
  const computeVolumesUrl = useTopconVolumesApi
    ? `${API_URL}/api/v1/compute-volumes`
    : `${API_URL}/api/v1/compute-volumes-rfq`;

  const requestToken = annotationSourceVolumesToken;
  dispatch({ type: "SET_FLUID_VOLUMES_SUBMITTING", submitting: true });
  try {
    const response = await axios.post(
      computeVolumesUrl,
      etdrsVolumesApiPayload,
      {
        headers: {
          "Content-Type": "application/json",
        },
      },
    );

    if (response.status === 200) {
      if (!isAnnotationSourceVolumesTokenCurrent(requestToken)) {
        return null;
      }
      const existingScreenshotUrl =
        activeDicomData?.mainScreenShotUrl ||
        store.getState().dcm?.mainScreenShotUrl;
      if (existingScreenshotUrl) {
        dispatch({
          type: "mainScreenShot_url_changed",
          mainScreenShotUrl: null,
        });
      }

      const [centerX, centerY] = etdrsVolumesApiPayload.etdrs_center || [];
      if (Number.isFinite(centerX) && Number.isFinite(centerY)) {
        dispatch({
          type: "SET_PREV_START_REF",
          payload: { x: centerX, y: centerY },
        });
      }

      const responseFluidLabels = getFluidLabelsFromVolumesData(response.data);
      if (responseFluidLabels.length) {
        dispatch({
          type: "SET_ACTIVE_FLUID_LABELS",
          activeFluidLabels: responseFluidLabels,
        });
      } else if (!useTopconVolumesApi) {
        dispatch({
          type: "SET_ACTIVE_FLUID_LABELS",
          activeFluidLabels: getFluidLabelsForVolumesApi(manufacturer),
        });
      }

      dispatch({
        type: "SET_ETDRS_VOLUMES_FROM_SEGMENTATION_API",
        etdrsVolumesFromSegmentationApi: response.data,
      });

      const opEl = getOpCornerstoneElement(activeDicomData);
      const appliedOnElement = applyEtdrsVolumesToOpElement(
        opEl,
        response.data,
      );
      if (opEl && appliedOnElement) {
        try {
          cornerstone.updateImage(opEl);
        } catch (_e) {
          /* noop */
        }
      }
      if (!appliedOnElement) {
        applyEtdrsVolumesToRedux(stack_id, response.data, activeDicomData);
      }
      syncOpAnnotationsFromElement(store.getState().dcm, stack_id);

      // Volumes render only when no dirty fluid frames; clear immediately so grid
      // updates before the delayed IR screenshot and save-annotations call.
      dispatch({ type: "CLEAR_ALL_FLUID_DIRTY_FRAMES" });
      dispatch({ type: "SET_FLUID_VOLUMES_SUBMITTING", submitting: false });

      const opElForRepaint = getOpCornerstoneElement(store.getState().dcm);
      if (opElForRepaint) {
        try {
          cornerstone.updateImage(opElForRepaint);
        } catch (_e) {
          /* noop */
        }
      }

      await captureIrScreenshot(store.getState().dcm, stack_id, dispatch);

      dispatch({ type: "LoadingOverlay_changed", LoadingOverlay: false });

      if (saveParams) {
        await saveAnnotationsAfterVolumes(
          store.getState().dcm,
          dispatch,
          {
            stack_id,
            ...saveParams,
          },
          response.data,
        );
      }
      return response.data;
    }
  } catch (error) {
    console.error("Error calling mat_compute_volume API:", error);
    if (error.response) {
      console.error("Error response:", error.response.data);
      console.error("Error status:", error.response.status);
    }
    throw error;
  } finally {
    dispatch({ type: "SET_FLUID_VOLUMES_SUBMITTING", submitting: false });
  }
};
